import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ResponseObject } from '@app/class/responseObject';
import { RequestService } from '@commonServices/request.service';
import { Client as AccountAPI, UserNameInfoDto } from '@interfaces/HttpClient/AccountApiModels';
import { Client as CatalogAPI, CatalogBranchDto, CatalogLiveStateDto, ExcelTemplateLanguageType, ImportCatalogSettingsDto, ImportHistoryDto, PrepareImportForUploadDto, PreviewCatalogCreateDto, PreviewCatalogCreateInfoDto, TokenDto, UploadFileTargetDto } from '@interfaces/HttpClient/CatalogApiModels';
import { firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdministrationService } from './administration.service';
import { CatalogUploadService } from './catalog-upload.service';
@Injectable()
export class ImportCatalogService {
	constructor(
		private requestService: RequestService,
		private catalogApi: CatalogAPI,
		private accountAPI: AccountAPI,
		private administrationService: AdministrationService,
		private catalogUploadService: CatalogUploadService,
	) { }

	// archive
	async getImportHistoryByCatalogId(
		catalogId: number
	): Promise<ImportHistoryDto[]> {
		try {
			return await firstValueFrom(this.catalogApi
				.importCatalog_GetImportHistoryByCatalogId(catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCurrentImportsByCatalogId(
		catalogId: number
	): Promise<ImportHistoryDto[]> {
		try {
			return await firstValueFrom(this.catalogApi
				.importCatalog_GetLiveAndPreviewImports(catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogBranchesByCatalogId(
		catalogId: number
	): Promise<CatalogBranchDto[]> {
		try {
			return await firstValueFrom(this.catalogApi
				.importCatalog_GetCatalogBranchesByCatalogId(catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getCatalogLiveStateById(
		catalogId: number
	): Promise<CatalogLiveStateDto> {
		try {
			return await firstValueFrom(this.catalogApi
				.importCatalog_GetCatalogLiveStateById(catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	// import
	async getUploadFileTarget(catalogId: number, fileName: string): Promise<UploadFileTargetDto> {
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_GetUploadFileTarget(catalogId, fileName)
				.pipe(takeUntil(this.catalogUploadService.getCancelCall())));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async createCatalogManagerBranch(dto: PreviewCatalogCreateDto): Promise<CatalogBranchDto> {
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_CreateCatalogManagerBranch(dto)
				.pipe(takeUntil(this.catalogUploadService.getCancelCall())));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async prepareImportForUpload(prepareImportForUploadDto: PrepareImportForUploadDto): Promise<void> {
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_PrepareImportForUpload(prepareImportForUploadDto)
				.pipe(takeUntil(this.catalogUploadService.getCancelCall())));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}



	async getImportCatalogSettings(catalogId?: number): Promise<ImportCatalogSettingsDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_GetImportCatalogSettings(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async updateImportCatalogSettings(importCatalogSettingsDto: ImportCatalogSettingsDto): Promise<ImportCatalogSettingsDto> {
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_UpdateImportCatalogSettings(importCatalogSettingsDto));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async reimport(importId: number): Promise<ResponseObject> {
		try {
			await firstValueFrom(this.catalogApi.importCatalog_Reimport(importId))
			return { success: true, message: "" };
		} catch (error) {
			return { success: false, message: error.message };
		}
	}

	async getImportArchiveFile(importId: number): Promise<TokenDto> {
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_GetImportArchiveFile(importId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getImportArchives(): Promise<ImportHistoryDto[]> {
		try {
			return await firstValueFrom(this.catalogApi.importArchive_GetImportArchives());
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getExcelTemplate(language: ExcelTemplateLanguageType, catalogId?: number): Promise<TokenDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_GetExcelTemplateFile(language, id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async getPreviewCatalogCreateInfo(catalogId?: number): Promise<PreviewCatalogCreateInfoDto> {
		const id = catalogId ? catalogId : this.administrationService.getId();
		try {
			return await firstValueFrom(this.catalogApi.importCatalog_GetPreviewCatalogCreateInfo(id));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async isCadenasCatalog(catalogName: string, catalogId: number): Promise<boolean> {
		try {
			return await firstValueFrom(this.catalogApi.cadenasProcurement_CadenasCatalog(catalogName, catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async isCadClickCatalog(catalogUrl: string, catalogId: number): Promise<boolean> {
		try {
			return await firstValueFrom(this.catalogApi.cadClick_CadClickCatalog(catalogUrl, catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async isDgnbCatalog(manufacturer: string, catalogId: number): Promise<boolean> {
		try {
			return await firstValueFrom(this.catalogApi.dgnb_DgnbCatalog(manufacturer, catalogId));
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}

	async resolveUserNames(catalogId: number, idpIds: string[]): Promise<UserNameInfoDto[]> {
		try {
			return idpIds.length > 0 ? await firstValueFrom(this.accountAPI.importCatalog_GetUserNames(catalogId, idpIds)) : [];
		} catch (e) {
			return this.requestService.errorHandling(e, null, [
				{ status: HttpStatusCode.NoContent, return: undefined }
			]);
		}
	}


}
